/**
 * Rising Auctions – server clock.
 *
 * Auction end times (countdownEndsAt) and payment deadlines (paymentExpirationAt) are absolute
 * instants issued by the RA backend. Ticking them against Date.now() means ticking against the
 * shopper's device clock: a device 30 minutes fast shows every auction as ending 30 minutes early
 * (timer stuck at 00:00:00, bid controls hidden) and a slow device shows auctions running past
 * their real end. Bidding itself is safe — the backend validates the auction window against its
 * own clock — but what the shopper sees is wrong.
 *
 * This module keeps one offset between the device clock and the RA server clock, and exposes
 * RAServerClock.now() to be used everywhere Date.now() would drive a countdown.
 *
 * Two ways to feed it:
 *   - syncFromPayload(json, sentAt) – every RA app proxy response carries `serverTime` (epoch ms).
 *     This is the free path: no extra request, and it lands before the countdown is rendered.
 *   - sync() – explicit GET /apps/rising-auctions/server-time, for pages that tick before any
 *     data call and for re-syncs after the tab has been hidden.
 *
 * Failing to sync is not fatal: the offset stays 0, which is exactly the previous behaviour.
 * Load this before the other rising-auctions-*.js assets.
 */
(function () {
  'use strict';

  if (window.RAServerClock) return;

  var SERVER_TIME_URL = '/apps/rising-auctions/server-time';
  /** Re-sync cadence for long-lived pages; device clocks drift and can be corrected mid-session. */
  var RESYNC_INTERVAL_MS = 15 * 60 * 1000;
  /** A sync older than this is refreshed when the tab becomes visible again (sleep/wake drift). */
  var STALE_AFTER_MS = 5 * 60 * 1000;
  /** Samples slower than this leave too much uncertainty in the one-way estimate to be useful. */
  var MAX_USABLE_ROUND_TRIP_MS = 10000;

  /** serverNow - Date.now(). 0 until the first successful sync. */
  var offset = 0;
  /** Device-clock time of the last accepted sample; 0 = never synced. */
  var syncedAt = 0;
  var inFlight = null;
  var resyncTimer = null;

  function applySample(serverTime, sentAt, receivedAt) {
    if (typeof serverTime !== 'number' || !isFinite(serverTime)) return false;
    var roundTrip = receivedAt - sentAt;
    if (roundTrip < 0 || roundTrip > MAX_USABLE_ROUND_TRIP_MS) return false;
    // serverTime was true midway through the round trip; assume the legs are symmetric.
    offset = serverTime + roundTrip / 2 - receivedAt;
    syncedAt = receivedAt;
    return true;
  }

  /**
   * Feed any RA app proxy response that carries serverTime.
   * `sentAt` must be Date.now() captured immediately before the fetch; without it the round trip
   * cannot be corrected for and the offset is short by the response leg.
   */
  function syncFromPayload(json, sentAt) {
    if (!json || typeof json.serverTime !== 'number') return false;
    var receivedAt = Date.now();
    return applySample(json.serverTime, typeof sentAt === 'number' ? sentAt : receivedAt, receivedAt);
  }

  function sync() {
    if (inFlight) return inFlight;
    var sentAt = Date.now();
    inFlight = fetch(SERVER_TIME_URL, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (json) {
        syncFromPayload(json, sentAt);
      })
      .catch(function () {
        // Keep the last known offset (0 when never synced = device clock, previous behaviour).
      })
      .then(function () {
        inFlight = null;
      });
    return inFlight;
  }

  /** Sync only if this page has never had a sample; safe to call from any countdown starter. */
  function ensureSynced() {
    if (syncedAt !== 0) return null;
    return sync();
  }

  function isStale() {
    return syncedAt === 0 || Date.now() - syncedAt > STALE_AFTER_MS;
  }

  /** Use instead of Date.now() wherever the value drives a countdown or an expiry check. */
  function now() {
    return Date.now() + offset;
  }

  function startResyncTimer() {
    if (resyncTimer != null) return;
    resyncTimer = setInterval(function () {
      // Only pages that actually use the clock keep refreshing it.
      if (syncedAt !== 0) sync();
    }, RESYNC_INTERVAL_MS);
  }

  document.addEventListener('visibilitychange', function () {
    // A tab that was hidden may have been through a sleep/wake cycle, or the OS may have
    // corrected the clock in the meantime; both invalidate the offset.
    if (document.visibilityState === 'visible' && syncedAt !== 0 && isStale()) sync();
  });

  startResyncTimer();

  window.RAServerClock = {
    now: now,
    sync: sync,
    ensureSynced: ensureSynced,
    syncFromPayload: syncFromPayload,
    /** Debugging: current device→server offset in ms, and whether a sample was ever accepted. */
    getOffset: function () {
      return offset;
    },
    isSynced: function () {
      return syncedAt !== 0;
    },
  };
})();
